import type { ReactNode } from 'react';
import { Icon } from '@/features/common/Icon';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicLayout } from '@/i18n/dictionaries/publicLayout';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/common/layout/gov-bar.css';

/**
 * The government service bar that sits above every page in both zones.
 *
 * This existed twice: as `layout/header.html`'s `gov-bar` fragment for the server-rendered
 * public site, and hand-written again inside AppShell for the cabinet. The two agreed on the
 * markup and disagreed on everything else — the public one switched language with a `?lang=`
 * round trip through SessionLocaleResolver, the cabinet one flipped React state. Now that both
 * zones are React, the client-side switch is the only one that makes sense, and the server
 * round trip goes with the template.
 *
 * `links` rather than a `variant` prop: the two zones differ only in what sits left of the
 * language switch — the public bar offers Accessibility and Sitemap, the cabinet offers
 * Accessibility alone, because its way back to the public zone is the AKIS logo below. A
 * boolean would have to be read against this comment to know what it does; the children say it.
 *
 * `contained` wraps the row in `.container`, which the public site uses to hold the bar to the
 * same max-width as the page beneath it. The cabinet spans the full viewport and passes nothing.
 */
export function GovBar({ links, contained = false }: { links?: ReactNode; contained?: boolean }) {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation(publicLayout);

  const row = (
    <div className={contained ? 'container gov-bar__inner' : 'gov-bar__inner'}>
      <p className="gov-bar__official">
        <Icon name="arrow-left-gov" className="gov-bar__mark" />
        <strong>{t('govbarOfficial')}</strong>
        <span>· {t('govbarMinistry')}</span>
      </p>
      <div className="gov-bar__right">
        {links}
        <nav className="lang-switch" aria-label={t('langSwitchAria')}>
          {(['ro', 'en'] as const).map((code) => (
            <button
              key={code}
              type="button"
              className={'lang-switch__toggle' + (language === code ? ' active' : '')}
              // aria-current, not aria-pressed: this is "which one are you on", the same
              // question the nav answers, and it is what the existing e2e specs assert.
              aria-current={language === code ? 'true' : undefined}
              lang={code}
              onClick={() => setLanguage(code)}
            >
              {code.toUpperCase()}
            </button>
          ))}
          {/* Slides between the two labels; positioned by CSS off .active, so it needs no state. */}
          <span className="lang-switch__thumb" aria-hidden="true" />
        </nav>
      </div>
    </div>
  );

  return <div className="gov-bar">{row}</div>;
}
